import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SemiconLayout from './SemiconLayout';
import { useAuth } from '../../../context/AuthContext';
import { loadWarpageSnapshot } from './warpageMath';
import { loadPeelSnapshot } from './peelModel';
import { RUBRIC, DEFENCE_NOTES } from './defenceNotes';
import NotesPanel from './NotesPanel';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

const DefenceBench = () => {
  const { token, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  // Present only when this bench was opened from a course's capstone lesson —
  // lets the grade be recorded against that capstone instead of evaporating.
  const capstoneContext = useMemo(() => {
    const courseId = searchParams.get('courseId');
    const si = searchParams.get('si');
    const li = searchParams.get('li');
    if (!courseId || si === null || li === null) return null;
    return { courseId, sectionIndex: Number(si), lessonIndex: Number(li) };
  }, [searchParams]);
  const warpage = useMemo(() => loadWarpageSnapshot(), []);
  const peel = useMemo(() => loadPeelSnapshot(), []);
  const [call, setCall] = useState('process-mechanical');
  const [monday, setMonday] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const evidence = [
    warpage ? `Bow ${Number(warpage.bowUm).toFixed(1)} µm on ${warpage.lengthMm || '?'} mm, ΔT ${warpage.deltaT} K, spec ${warpage.spec || 'n/a'}, die bending ~${warpage.dieStressMpa != null ? Number(warpage.dieStressMpa).toFixed(0) : '?'} MPa` : null,
    peel?.lastPeel != null ? `Last peel ${peel.lastPeel.toFixed(2)} N/mm (plasma ${peel.lastPlasma || '?'}, n=${peel.n || 1})` : null,
    peel?.dominant ? `Largest peel main effect: ${peel.dominant} (Δ plasma ${peel.effects?.plasma?.toFixed?.(2)}, cure ${peel.effects?.cure?.toFixed?.(2)}, DA ${peel.effects?.adhesive?.toFixed?.(2)} N/mm)` : null,
  ].filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setError('Sign in. A senior will not review a ghost.');
      return;
    }
    const brief = [
      `Call: ${call}.`,
      evidence.length ? `Evidence: ${evidence.join('; ')}.` : 'Evidence: none logged from the benches.',
      why.trim(),
      `Monday: ${monday.trim()}`,
    ].join(' ');
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/virtual-labs/semicon-mech/defence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ brief, context: capstoneContext || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Defence failed');
      setResult(data.data || data);
    } catch (err) {
      setError(err.message || 'The senior did not pick up.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SemiconLayout
      kicker="Station 3 · 8:12 stand-up"
      title="Defend the lot"
      lead="You already bowed a strip and pulled a joint. Now say what is going on in one breath a packaging senior will not laugh at."
    >
      <div className="sm-metrics">
        {evidence.length ? evidence.map((e) => (
          <span key={e} className="sm-metric"><b>From the line</b> · {e}</span>
        )) : (
          <span className="sm-metric">No bench data yet — run warpage and peel first, or go in cold and get failed for it.</span>
        )}
      </div>
      <table className="sm-rubric">
        <thead>
          <tr><th>Rubric</th><th>Fail</th><th>Pass</th></tr>
        </thead>
        <tbody>
          {RUBRIC.map((r) => (
            <tr key={r.id}>
              <td><b>{r.label}</b></td>
              <td>{r.fail}</td>
              <td>{r.pass}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <form onSubmit={submit} className="sm-split">
        <div className="sm-controls">
          <div className="sm-toggles" style={{ gridTemplateColumns: '1fr' }}>
            {[
              ['process-mechanical', 'Process-mechanical', 'Pad, plasma, cool-down, fixture'],
              ['materials', 'Materials', 'EMC / DA chemistry'],
              ['need-data', 'Need more data', 'Do not guess the lot'],
            ].map(([id, title, sub]) => (
              <button key={id} type="button" className={`sm-toggle ${call === id ? 'is-on' : ''}`} onClick={() => setCall(id)}>
                <strong>{title}</strong><span>{sub}</span>
              </button>
            ))}
          </div>
          <label className="sm-field">Why — physics in two sentences
            <textarea rows={4} required minLength={40} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Bow comes from… Peel moved when…" />
          </label>
          <label className="sm-field">Monday action
            <input required value={monday} onChange={(e) => setMonday(e.target.value)} placeholder="Plasma the pad. Do not change DA this week." />
          </label>
          <button type="submit" className="sm-btn" disabled={busy}>{busy ? 'In the room…' : 'Walk in'}</button>
          {error && <p className="sm-note">{error}</p>}
        </div>
        <div>
          <div className="sm-card">
            <em>You</em>
            <h2>{call.replace('-', ' ')}</h2>
            <p>{why || 'No argument yet.'}</p>
            <p className="sm-note">{monday || 'No Monday action.'}</p>
          </div>
          {result && (
            <div className="sm-chat">
              <h3>{result.passed ? 'Senior · you own the next lot' : 'Senior · rewrite it'}</h3>
              <p className="sm-note" style={{ color: '#fde68a', whiteSpace: 'pre-wrap' }}>{result.critique}</p>
              {capstoneContext && (
                <p className="sm-note">
                  {result.recorded
                    ? 'Recorded to your capstone — visible to your mentor for review.'
                    : 'Not recorded to a capstone (open this bench from a capstone lesson to log it).'}
                </p>
              )}
            </div>
          )}
          <p className="sm-note">
            <Link to="/virtuallabs/semicon-mech/warpage" className="sm-back">Re-run warpage</Link>
            {' · '}
            <Link to="/virtuallabs/semicon-mech/peel-shear" className="sm-back">Re-pull peel</Link>
          </p>
        </div>
      </form>
      <NotesPanel title="How this review is graded" items={DEFENCE_NOTES} />
    </SemiconLayout>
  );
};

export default DefenceBench;
